import {AfterContentChecked, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormArray, FormBuilder, Validators} from '@angular/forms';
import {KfRegistrationDetail} from '../../models/kf-registration-detail.interface';
import {QuantitativeDetail} from '../../models/quantitative-detail.interface';
import {Master} from '../../../admin/models/master.interface';
import {Router} from '@angular/router';
import {KfRegistrationService} from '../../services/kf-registration.service';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {User} from '../../models/user.interface';
import {GenericDialogComponent} from '../../../shared/components/generic-dialog/generic-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {AuthService} from '../../../core/services/auth.service';

@Component({
  selector: 'app-kf-registration-edit-table-differentiated',
  templateUrl: './kf-registration-edit-table-differentiated.component.html',
  styleUrls: ['./kf-registration-edit-table-differentiated.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      )
    ])
  ]
})
export class KfRegistrationEditTableDifferentiatedComponent implements OnInit, AfterContentChecked {

  simpleUser = true;
  totalValues = [0, 0];
  totalB2BValues = [0, 0];
  totalB2CValues = [0, 0];
  categoriesDuplicated: Master[] = [];
  displayedColumns: string[] = ['entry', 'pieces', 'tons', 'remark'];
  b2bFooter: string[] = ['b2bFooter', 'totalB2BPieces', 'totalB2BTons', 'remark'];
  b2cFooter: string[] = ['b2cFooter', 'totalB2CPieces', 'totalB2CTons', 'remark'];

  entriesFormDuplicated = this.formBuilder.group({
    entries: this.formBuilder.array([this.formBuilder.group({
      pieces: [0],
      tons: [0],
      piecesDifferentValueAllowed: [false],
      tonsDifferentValueAllowed: [false],
      remark: [''],
    })])
  });

  get entriesControl(): FormArray {
    return this.entriesFormDuplicated.get('entries') as FormArray;
  }

  @Input()
  kfRegistrationDetail!: KfRegistrationDetail;
  @Input()
  originalDetailLevel!: string;
  @Input()
  newKfRegistrationDetail!: KfRegistrationDetail;
  @Input()
  categories!: Master[];
  @Input()
  b2bb2c!: string;
  @Input()
  country!: boolean;
  @Input()
  year!: number;
  @Input()
  keyFigure!: string;
  @Input()
  user!: User;

  @Output()
  markAsCompletedEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  markAsCompletedWarningEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  resetEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  redirectUrlEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(private readonly formBuilder: FormBuilder,
              private readonly kfRegistrationService: KfRegistrationService,
              private readonly router: Router,
              private readonly dialog: MatDialog,
              private readonly changeDetector: ChangeDetectorRef,
              private readonly authService: AuthService) {
  }

  ngOnInit(): void {
    this.simpleUser = this.authService.userHasAnyRole(['SIMPLE_USER']);
    if ((this.kfRegistrationDetail?.categories?.length !== this.categories?.length && this.originalDetailLevel != "DIFFERENTIATED")) {
      this.categories?.forEach((category) => {
        this.categoriesDuplicated.push(category);
        this.categoriesDuplicated.push(category);
      });
    } else {
      if (this.originalDetailLevel == "UNDIFFERENTIATED") {
        this.categories?.forEach((category) => {
          this.categoriesDuplicated.push(category);
          this.categoriesDuplicated.push(category);
        });
      } else {
        this.categoriesDuplicated = this.kfRegistrationDetail.categories
          .map((cat) => {
            return {
              id: cat.keyFiguresCategory?.id, code: cat.keyFiguresCategory?.code, description: cat.keyFiguresCategory?.description,
              kind: cat.kind, comment: cat.keyFiguresCategory?.comment, disabled: cat.keyFiguresCategory?.disabled
            } as Master;
          });
      }
      }
    if (this.kfRegistrationDetail?.kfRegistration && this.kfRegistrationDetail?.categories && this.originalDetailLevel != "UNDIFFERENTIATED") {
      this.setEntriesFormArray(this.kfRegistrationDetail.categories.length, this.kfRegistrationDetail);
      const categories: any = [];
      this.entriesControl.controls.forEach(((entry, index) => {
        categories.push({
          tons: this.kfRegistrationDetail.categories[index]?.tonsValue !== 0
            ? this.kfRegistrationDetail.categories[index]?.tonsValue
            : (this.kfRegistrationDetail.kfRegistration.completionStatus ? this.kfRegistrationDetail.categories[index]?.tonsValue : null),
          pieces: this.kfRegistrationDetail.categories[index]?.piecesValue !== 0
            ? this.kfRegistrationDetail.categories[index]?.piecesValue
            : (this.kfRegistrationDetail.kfRegistration.completionStatus ? this.kfRegistrationDetail.categories[index]?.piecesValue : null),
          remark: this.kfRegistrationDetail.categories[index]?.remark
        });
      }));
      this.entriesControl.patchValue(categories);
    } else {
      this.setEntriesFormArray((this.categories.length * 2), this.newKfRegistrationDetail);
    }

    this.sum();
    if (this.simpleUser) {
      this.entriesFormDuplicated.disable();
    }
  }

  ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }

  allowValidation(controlName: string, index: number): void {
    this.entriesControl.at(index).get(controlName)?.clearValidators();
    this.entriesControl.at(index).get(controlName)?.updateValueAndValidity();

    this.entriesControl.at(index).get(controlName + 'DifferentValueAllowed')?.setValue(true);
  }

  markAsCompleted(): void {

    if (this.kfRegistrationDetail) {
      this.kfRegistrationDetail.categories = this.setEntriesValues();
      this.kfRegistrationService.saveKfRegistration(this.kfRegistrationDetail, this.router.url.split('/')[4]).subscribe(() => {
        this.doMarkAsCompleted();
      });
    } else {
      this.newKfRegistrationDetail.categories = this.setEntriesValues();
      this.kfRegistrationService.saveKfRegistration(this.newKfRegistrationDetail, this.router.url.split('/')[4]).subscribe(() => {
        this.doMarkAsCompleted();
      });
    }
  }

  private doMarkAsCompleted = () => {
    let totalPieces = 0;
    let totalTons = 0;
    if (this.country) { // kf registration country
      // get kf registration weee system
      this.kfRegistrationService.getKfRegistration(
        this.keyFigure.replace(/ /g, '').toLowerCase(),
        this.year,
        this.user.weeeSystem.id,
        'system').subscribe(
        (data) => {
          if (data.categories) {
            data.categories.forEach((category) => {
              totalPieces += category.piecesValue;
              totalTons += category.tonsValue;
            });
            if (this.totalValues[0] >= totalPieces || this.totalValues[1] >= totalTons) {
              this.markAsCompletedEmitter.emit();
            } else {
              if (this.keyFigure === 'Put On Market' || this.keyFigure === 'Collected') {
                this.checkPomAndCollectedWarningAndMarkCompleted('country');
              } else {
                this.markAsCompletedEmitter.emit();
              }
            }
          } else {
            if (this.keyFigure === 'Put On Market' || this.keyFigure === 'Collected') {
              this.checkPomAndCollectedWarningAndMarkCompleted('country');
            } else {
              this.markAsCompletedEmitter.emit();
            }
          }
        }
      );
    } else { // kf registration weee system
      // get kf registration country
      this.kfRegistrationService.getKfRegistration(
        this.keyFigure.replace(/ /g, '').toLowerCase(),
        this.year,
        this.user.weeeSystem.country.id,
        'country').subscribe(
        (data) => {
          if (data.categories) {
            data.categories.forEach((category) => {
              totalPieces += category.piecesValue;
              totalTons += category.tonsValue;
            });
            if ((totalPieces === 0 || this.totalValues[0] <= totalPieces) && (totalTons === 0 || this.totalValues[1] <= totalTons)) {
              if (this.keyFigure === 'Put On Market' || this.keyFigure === 'Collected') {
                this.checkPomAndCollectedWarningAndMarkCompleted('system');
              } else {
                this.markAsCompletedEmitter.emit();
              }
            } else {
              this.markAsCompletedWarningEmitter.emit();
            }
          } else {
            if (this.keyFigure === 'Put On Market' || this.keyFigure === 'Collected') {
              this.checkPomAndCollectedWarningAndMarkCompleted('system');
            } else {
              this.markAsCompletedEmitter.emit();
            }
          }
        }
      );
    }
  }

  reset(): void {
    this.resetEmitter.emit();
  }

  entriesDuplicatedSubmit(): void {
    if (this.kfRegistrationDetail) {
      this.kfRegistrationDetail.categories = this.setEntriesValues();
      this.kfRegistrationService.saveKfRegistration(this.kfRegistrationDetail, this.router.url.split('/')[4]).subscribe(() => {
        this.redirectUrlEmitter.emit(false);
      });
    } else {
      this.newKfRegistrationDetail.categories = this.setEntriesValues();
      this.kfRegistrationService.saveKfRegistration(this.newKfRegistrationDetail, this.router.url.split('/')[4]).subscribe(() => {
        this.redirectUrlEmitter.emit(false);
      });
    }
  }

  private setEntriesFormArray(elements: number, kfRegistrationDetail: KfRegistrationDetail): void {
    this.entriesControl.clear();
    for (let i = 0; i < elements; i++) {
      // comparar b2bb2c del año pasado con el actual para comparar o no la categorias
      // comprobar que haya categorias el año pasado
      // comprobar que la categoría sea del constructor (que no se guardaron datos para poner restricciones)
      if (kfRegistrationDetail.lastYearDetailLevel === this.b2bb2c &&
        kfRegistrationDetail?.lastYearCategories !== undefined &&
        !kfRegistrationDetail?.kfRegistration?.completionStatus) {
        const lastYearCategoryB2B = kfRegistrationDetail.lastYearCategories
          .find(cat => cat.keyFiguresCategory?.id === this.categoriesDuplicated[i].id && cat.kind.toString() === 'B2B');
        const lastYearCategoryB2C = kfRegistrationDetail.lastYearCategories
          .find(cat => cat.keyFiguresCategory?.id === this.categoriesDuplicated[i].id && cat.kind.toString() === 'B2C');

        // si existen valores para la categoria en el año anterior, aplicamos las restricciones
        if (lastYearCategoryB2B || lastYearCategoryB2C) {
          const lastYearCategory = (i % 2 === 0) ? lastYearCategoryB2B : lastYearCategoryB2C;
          if (lastYearCategory) {
            this.entriesControl.push(
              this.formBuilder.group({
                pieces: [null, (!kfRegistrationDetail?.categories[i]?.piecesDifferentValueAllowed && lastYearCategory.piecesValue) ?
                  [
                    Validators.max(lastYearCategory.piecesValue + (kfRegistrationDetail?.deviation / 100 * lastYearCategory.piecesValue)),
                    Validators.min(lastYearCategory.piecesValue - (kfRegistrationDetail?.deviation / 100 * lastYearCategory.piecesValue))
                  ] : []
                ],
                tons: [null, (!kfRegistrationDetail?.categories[i]?.tonsDifferentValueAllowed  && lastYearCategory.tonsValue) ?
                  [
                    Validators.max(lastYearCategory.tonsValue + (kfRegistrationDetail?.deviation / 100 * lastYearCategory.tonsValue)),
                    Validators.min(lastYearCategory.tonsValue - (kfRegistrationDetail?.deviation / 100 * lastYearCategory.tonsValue))
                  ] : []
                ],
                piecesDifferentValueAllowed: [kfRegistrationDetail?.categories[i]?.piecesDifferentValueAllowed
                  ? kfRegistrationDetail.categories[i]?.piecesDifferentValueAllowed
                  : false
                ],
                tonsDifferentValueAllowed: [kfRegistrationDetail?.categories[i]?.tonsDifferentValueAllowed
                  ? kfRegistrationDetail.categories[i]?.tonsDifferentValueAllowed
                  : false
                ],
                remark: [''],
              })
            );
          } else {
            this.entriesControl.push(
              this.formBuilder.group({
                pieces: [null, []],
                tons: [null, []],
                remark: [''],
                piecesDifferentValueAllowed: [false],
                tonsDifferentValueAllowed: [false],
              })
            );
          }
        } else {
          this.entriesControl.push(
            this.formBuilder.group({
              pieces: [null, []],
              tons: [null, []],
              remark: [''],
              piecesDifferentValueAllowed: [false],
              tonsDifferentValueAllowed: [false],
            })
          );
        }
      } else {
        this.entriesControl.push(
          this.formBuilder.group({
            pieces: [null, []],
            tons: [null, []],
            remark: [''],
            piecesDifferentValueAllowed: [false],
            tonsDifferentValueAllowed: [false],
          })
        );
      }
    }
  }

  private setEntriesValues(): QuantitativeDetail[] {
    const quantitativeDetail: QuantitativeDetail[] = [];
    this.entriesControl.value.forEach(
      (entry: { pieces: 0, tons: 0, piecesDifferentValueAllowed: false, tonsDifferentValueAllowed: false, remark: '' }, index: number) => {
        if (this.categoriesDuplicated[index]) {
          const qd: QuantitativeDetail = {
            clearingHouse: 0, collection: 0, communication: 0, rd: 0, logistics: 0, office: 0, other: 0,
            treatment: 0, operationsRemark: '', overheadRemark: '',
            id: this.kfRegistrationDetail?.categories[index]?.id,
            keyFiguresCategory: this.categoriesDuplicated[index],
            piecesValue: entry.pieces === null ? 0 : entry.pieces,
            tonsValue: entry.tons === null ? 0 : entry.tons,
            piecesDifferentValueAllowed: entry.piecesDifferentValueAllowed,
            tonsDifferentValueAllowed: entry.tonsDifferentValueAllowed,
            remark: entry.remark,
            kind: index === 0 || index % 2 === 0 ? 1 : 2,

            collectionDifferentValueAllowed: false,
            logisticsDifferentValueAllowed: false,
            treatmentDifferentValueAllowed: false,
            clearingHouseDifferentValueAllowed: false,
            communicationDifferentValueAllowed: false,
            rdDifferentValueAllowed: false,
            officeDifferentValueAllowed: false,
            otherDifferentValueAllowed: false,
            er: 0,
            erDifferentValueAllowed: false,
            ld: 0,
            ldDifferentValueAllowed: false,
            loss: 0,
            lossDifferentValueAllowed: false,
            omr: 0,
            omrDifferentValueAllowed: false,
            mr: 0,
            mrDifferentValueAllowed: false,
            ru: 0,
            ruDifferentValueAllowed: false,
            td: 0,
            tdDifferentValueAllowed: false,
          };
          quantitativeDetail.push(qd);
        }
      });
    return quantitativeDetail;
  }

  sum(): void {
    this.totalValues = [0, 0];
    this.totalB2BValues = [0, 0];
    this.totalB2CValues = [0, 0];
    this.entriesControl.value.forEach((value: any, index: number) => {
      if (value.pieces !== null && !Number.isNaN(value.pieces)) {
        this.totalValues[0] += value.pieces;
        if (this.isOdd(index)) {
          this.totalB2CValues[0] += value.pieces;
        } else {
          this.totalB2BValues[0] += value.pieces;
        }
      }
      if (value.tons !== null && !Number.isNaN(value.tons)) {
        this.totalValues[1] += value.tons;
        if (this.isOdd(index)) {
          this.totalB2CValues[1] += value.tons;
        } else {
          this.totalB2BValues[1] += value.tons;
        }
      }
    });
  }

  private isOdd = (num: number) => num % 2;

  checkPomAndCollectedWarningAndMarkCompleted(type: string): void {
    this.kfRegistrationService.checkPomAndCollectedValues
    (this.year, type === 'country' ? this.user.weeeSystem.country.id : this.user.weeeSystem.id, type)
      .subscribe(response => {
        if (!response.content) {
          const dialogRef = this.dialog.open(GenericDialogComponent,
            {
              data:
                {
                  title: `Warning`,
                  text: 'Some total values of the collection registration are higher than those of the POM registration.'
                    + '\n Do you want to continue and mark as completed?'
                }
            });
          dialogRef.componentInstance.confirm.subscribe((confirm: boolean) => {
            if (confirm) {
              this.markAsCompletedEmitter.emit();
            }
            dialogRef.close();
          });

          dialogRef.afterClosed().subscribe(() => {
            dialogRef.componentInstance.confirm.unsubscribe();
          });
        } else {
          this.markAsCompletedEmitter.emit();
        }
      });
  }

}
